import type { MessageKey } from '@dijitalapp/i18n';
import { INVITE_LINK_EXPIRY_HOURS, type CommunityDto, type ConversationDto, type SpaceDto } from '@dijitalapp/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChatStore } from '../lib/chat-context';

/** The shareable URL: the token rides in the fragment, which browsers never send to servers. */
export const spaceJoinUrl = (token: string) => `${location.origin}/space-join#${token}`;

/** Community view of a community or family space: announcements, groups, invite link and leaving. */
export function CommunityPanel({ space, onLeft }: { space: SpaceDto; onLeft: () => Promise<void> }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const navigate = useNavigate();
  const [community, setCommunity] = useState<CommunityDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [open, setOpen] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [expiry, setExpiry] = useState('never');
  const [copied, setCopied] = useState(false);
  const isAdmin = community?.myRole === 'owner' || community?.myRole === 'admin';

  const load = useCallback(() => {
    void api<CommunityDto>('GET', `/spaces/${space.id}/community`).then(setCommunity, (err: unknown) => setError(t(errorMessageKey(err))));
  }, [space.id, t]);
  // The panel is keyed by space, so a different space starts with fresh state (no stale token).
  useEffect(load, [load]);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  const openChat = async (id: string) => {
    await store?.loadConversations();
    navigate(`/messages/${id}`);
  };

  const createGroup = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api<ConversationDto>('POST', `/spaces/${space.id}/groups`, { title, open });
      setTitle('');
      load();
    });
  };

  const join = (groupId: string) =>
    run(async () => {
      await api<ConversationDto>('POST', `/spaces/${space.id}/groups/${groupId}/join`);
      await openChat(groupId);
    });

  const createLink = () =>
    run(async () => {
      const r = await api<{ token: string }>('POST', `/spaces/${space.id}/invite-link`, { expiresInHours: expiry === 'never' ? null : Number(expiry) });
      setToken(r.token);
      setCopied(false);
    });

  const revokeLink = () =>
    run(async () => {
      await api('DELETE', `/spaces/${space.id}/invite-link`);
      setToken(null);
    });

  const copy = async () => {
    if (!token) return;
    await navigator.clipboard.writeText(spaceJoinUrl(token)).then(() => setCopied(true), () => undefined);
  };

  const leave = () => {
    if (!window.confirm(t('community.leaveConfirm'))) return;
    void run(async () => {
      await api('DELETE', `/spaces/${space.id}/members/me`);
      await store?.loadConversations();
      await onLeft();
    });
  };

  return (
    <>
    <section className="card stack" aria-labelledby="community-title">
      <h2 id="community-title" className="card-title">
        {t('community.title')}
      </h2>
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}
      {!community && !error && <p className="muted">{t('app.loading')}</p>}
      {community && (
        <>
          <button className="list-item list-item-card" type="button" onClick={() => void openChat(community.announcementsId)}>
            <span className="avatar" aria-hidden>
              📢
            </span>
            <span style={{ minWidth: 0 }}>
              <strong>{t('community.announcements')}</strong>
              <br />
              <span className="muted">{isAdmin ? t('community.announcementsAdmin') : t('community.announcementsMember')}</span>
            </span>
          </button>

          <h3 className="subhead">{t('community.groups')}</h3>
          {community.groups.length === 0 && <p className="muted">{t('community.noGroups')}</p>}
          {community.groups.map((g) => (
            <div key={g.id} className="list-item list-item-card">
              <span className="avatar" aria-hidden>
                {Array.from(g.title)[0]?.toUpperCase()}
              </span>
              <span style={{ minWidth: 0 }}>
                <strong className="truncate">{g.title}</strong>
                <br />
                <span className="muted">
                  {t('group.memberCount', { count: g.memberCount })}
                  {!g.open && ` · ${t('community.closed')}`}
                </span>
              </span>
              <span className="spacer" />
              {g.joined ? (
                <button className="btn btn-sm" type="button" onClick={() => void openChat(g.id)}>
                  {t('join.open')}
                </button>
              ) : (
                g.open && (
                  <button className="btn btn-primary btn-sm" type="button" onClick={() => void join(g.id)}>
                    {t('join.join')}
                  </button>
                )
              )}
            </div>
          ))}

          {community.myRole !== 'owner' && (
            <div className="row">
              <button className="btn btn-sm btn-danger" type="button" onClick={leave}>
                {t('community.leave')}
              </button>
            </div>
          )}
        </>
      )}
    </section>
    {community && isAdmin && (
      <section className="card stack" aria-labelledby="community-admin">
        <h2 id="community-admin" className="card-title">
          {t('community.manage')}
        </h2>
              <form className="stack" onSubmit={createGroup}>
                <div className="field">
                  <label htmlFor="cg-title">{t('community.newGroup')}</label>
                  <input id="cg-title" className="input" required maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <label className="check">
                  <input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} />
                  <span>{t('community.openGroup')}</span>
                </label>
                <div className="row">
                  <span className="spacer" />
                  <button className="btn btn-primary">{t('group.create')}</button>
                </div>
              </form>

              <h3 className="subhead">{t('community.invite')}</h3>
              <p className="hint">{space.type === 'family' ? t('community.inviteFamilyNote') : t('community.inviteNote')}</p>
              {token && (
                <div className="stack" style={{ gap: 'var(--space-2)' }}>
                  <div className="row" style={{ flexWrap: 'nowrap' }}>
                    <label className="visually-hidden" htmlFor="cl-url">
                      {t('group.inviteLink')}
                    </label>
                    <input id="cl-url" className="input" readOnly value={spaceJoinUrl(token)} onFocus={(e) => e.currentTarget.select()} />
                    <button className="btn" type="button" onClick={() => void copy()}>
                      {copied ? t('group.linkCopied') : t('group.copyLink')}
                    </button>
                  </div>
                  <p className="hint">{t('group.linkOnce')}</p>
                </div>
              )}
              <div className="row">
                <label htmlFor="cl-expiry">{t('group.linkExpiry')}</label>
                <select id="cl-expiry" className="input" style={{ width: 'auto' }} value={expiry} onChange={(e) => setExpiry(e.target.value)}>
                  <option value="never">{t('group.expiry.never')}</option>
                  {INVITE_LINK_EXPIRY_HOURS.map((h) => (
                    <option key={h} value={String(h)}>
                      {t(`group.expiry.${h}` as MessageKey)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="row">
                <button className="btn btn-primary" type="button" onClick={() => void createLink()}>
                  {t('community.createLink')}
                </button>
                <button className="btn btn-danger" type="button" onClick={() => void revokeLink()}>
                  {t('group.revokeLink')}
                </button>
              </div>
      </section>
    )}
    </>
  );
}
